// Dashboard del módulo Hotel. Hotel → Resumen → Dashboard.
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle, BedDouble, CalendarClock, Gauge, LayoutDashboard, Loader2, LogIn, LogOut,
  Percent, ShieldAlert, Sparkles, TrendingUp, Wrench, type LucideIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useTabsStore } from '@/stores/tabs.store';
import { HotelDashboard, QK, fmtCorta, fmtFecha, hotelRpc, money, num } from './shared';
import { useAvisosHabitacionLista } from './avisosHk';
import { TareasPendientes } from './Huesped360';

export function DashboardHotel() {
  useAvisosHabitacionLista(); // 948: recepción se entera de las habitaciones que quedan listas
  const openTab = useTabsStore((s) => s.openTab);
  const ir = (path: string, title: string) => openTab({ id: path, title, path });

  const { data, isFetching } = useQuery<HotelDashboard>({
    queryKey: [QK, 'dashboard'],
    queryFn: () => hotelRpc<HotelDashboard>('dashboard'),
    staleTime: 0, refetchOnMount: 'always',
  });

  if (!data) {
    return (
      <div className="flex items-center gap-2 p-6 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" /> Cargando dashboard…
      </div>
    );
  }
  const hoy = data.hoy;

  const tiles: { icon: LucideIcon; label: string; valor: string; sub: string; alerta?: boolean; accion?: () => void }[] = [
    { icon: CalendarClock, label: 'Fecha de hotel', valor: fmtFecha(data.fecha_hotel), sub: `${num(data.habitaciones, 0)} habitaciones operativas` },
    { icon: Percent, label: 'Ocupación hoy', valor: `${num(hoy?.ocupacion_pct ?? 0, 1)}%`, sub: `${num(hoy?.ocupadas ?? 0, 0)} de ${num(data.habitaciones, 0)} habitaciones` },
    { icon: TrendingUp, label: 'ADR', valor: money(hoy?.adr ?? 0), sub: 'Tarifa promedio diaria' },
    { icon: Gauge, label: 'RevPAR', valor: money(hoy?.revpar ?? 0), sub: 'Ingreso por habitación disponible' },
    { icon: LogIn, label: 'Llegadas de hoy', valor: num(data.llegadas, 0), sub: 'Reservas por hacer check-in', accion: () => ir('/hotel/reservas', 'Reservas') },
    { icon: LogOut, label: 'Salidas de hoy', valor: num(data.salidas, 0), sub: 'Huéspedes por hacer check-out', accion: () => ir('/hotel/reservas', 'Reservas') },
    { icon: BedDouble, label: 'En casa', valor: num(data.en_casa, 0), sub: 'Huéspedes alojados ahora' },
    { icon: Sparkles, label: 'Sucias', valor: num(data.sucias, 0), sub: 'Pendientes de limpieza', alerta: data.sucias > 0, accion: () => ir('/hotel/housekeeping', 'Housekeeping') },
    { icon: Wrench, label: 'Fuera de servicio', valor: num(data.fuera_servicio, 0), sub: 'No disponibles para vender', alerta: data.fuera_servicio > 0, accion: () => ir('/hotel/housekeeping', 'Housekeeping') },
    { icon: TrendingUp, label: 'Pickup 7 días', valor: num(data.pickup_7d, 0), sub: 'Reservas nuevas en la última semana' },
  ];

  return (
    <div className="space-y-4 p-6">
      <h1 className="flex items-center gap-2 text-xl font-semibold">
        <LayoutDashboard className="h-5 w-5" /> Dashboard de Hotel
        {isFetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </h1>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        {tiles.map((t) => (
          <Card
            key={t.label}
            className={`${t.alerta ? 'border-amber-400' : ''} ${t.accion ? 'cursor-pointer hover:shadow-md' : ''}`}
            onClick={t.accion}
          >
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><t.icon className="h-4 w-4" /> {t.label}</div>
              <div className={`mt-1 text-2xl font-semibold font-mono tabular-nums ${t.alerta ? 'text-amber-600 dark:text-amber-400' : ''}`}>{t.valor}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{t.sub}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <TareasPendientes />

      {(data.tentativas_por_vencer > 0 || data.por_liquidar > 0) && (
        <div className="space-y-2">
          {data.tentativas_por_vencer > 0 && (
            <button
              type="button"
              onClick={() => ir('/hotel/reservas', 'Reservas')}
              className="flex w-full items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-left text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
            >
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span><b>{data.tentativas_por_vencer}</b> reserva(s) tentativa(s) con depósito por vencer en las próximas 12 horas — revísalas en Reservas.</span>
            </button>
          )}
          {data.por_liquidar > 0 && (
            <button
              type="button"
              onClick={() => ir('/hotel/reservas', 'Reservas')}
              className="flex w-full items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-left text-sm text-red-800 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span><b>{data.por_liquidar}</b> cancelación/no-show con penalidad por liquidar — revísalas en Reservas.</span>
            </button>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Ocupación de los próximos 30 días</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {data.proximos_30.map((d) => (
              <div key={d.fecha} className="flex items-center gap-2 text-xs">
                <span className="w-16 shrink-0 capitalize text-muted-foreground">{fmtCorta(d.fecha)}</span>
                <div className="h-3 flex-1 overflow-hidden rounded bg-muted">
                  <div
                    className={`h-full ${d.ocupacion_pct >= 90 ? 'bg-red-500' : d.ocupacion_pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                    style={{ width: `${Math.min(100, Number(d.ocupacion_pct))}%` }}
                  />
                </div>
                <span className="w-12 shrink-0 text-right font-mono tabular-nums">{num(d.ocupacion_pct, 0)}%</span>
              </div>
            ))}
            {!data.proximos_30.length && <p className="text-sm text-muted-foreground">Sin datos de ocupación</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Último cierre nocturno</CardTitle></CardHeader>
          <CardContent>
            {data.ultimo_cierre ? (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <KpiMini label="Fecha" valor={fmtFecha(data.ultimo_cierre.fecha)} />
                <KpiMini label="Ocupación" valor={`${num(data.ultimo_cierre.ocupacion_pct, 1)}%`} />
                <KpiMini label="ADR" valor={money(data.ultimo_cierre.adr)} />
                <KpiMini label="RevPAR" valor={money(data.ultimo_cierre.revpar)} />
                <KpiMini label="Ingreso hospedaje" valor={money(data.ultimo_cierre.ingreso_hospedaje)} />
                <KpiMini label="No-shows" valor={num(data.ultimo_cierre.no_shows, 0)} />
              </div>
            ) : <p className="text-sm text-muted-foreground">Todavía no se ha ejecutado ningún cierre.</p>}
            <button type="button" className="mt-3 text-xs text-primary underline" onClick={() => ir('/hotel/cierre', 'Cierre nocturno')}>
              Ir a Cierre nocturno
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiMini({ label, valor }: { label: string; valor: string }) {
  return (
    <div className="rounded-md border p-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-mono text-sm font-semibold tabular-nums">{valor}</div>
    </div>
  );
}
